<template>
  <div class="min-h-screen bg-gray-50 pt-20 lg:pt-28 pb-12 px-4">
    <div class="max-w-5xl mx-auto">
      <!-- En-tête -->
      <div class="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
        <div class="flex items-center justify-between mb-4">
          <h1 class="text-2xl md:text-3xl font-bold text-[#04406E]">
            Détails du Signalement
          </h1>
          <button
            @click="router.back()"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors text-sm cursor-pointer"
          >
            <span class="inline-flex items-center gap-2">
              <svg
                class="w-4 h-4 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span>Retour</span>
            </span>
          </button>
        </div>
        
        <div v-if="signalement" class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              ID Signalement
            </p>
            <p class="text-lg md:text-xl font-semibold text-[#04406E]">
              {{ signalement.id_signalement }}
            </p>
          </div>
          
          <div>
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              Code de suivi
            </p>
            <p class="text-base md:text-lg text-gray-900 font-mono">
              {{ signalement.code_suivi }}
            </p>
          </div>
          
          <div>
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              Statut
            </p>
            <span 
              :class="getStatusClass(signalement.statut)"
              class="inline-block px-3 py-1 rounded-full text-sm font-semibold"
            >
              {{ formatStatus(signalement.statut) }}
            </span>
          </div>
          
          <div>
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              Type de signalement
            </p>
            <p class="text-base md:text-lg text-gray-900">
              {{ signalement.type_signalement }}
            </p>
          </div>
          
          <div v-if="signalement.created_at">
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              Date de création
            </p>
            <p class="text-base md:text-lg text-gray-900">
              {{ formatDate(signalement.created_at) }}
            </p>
          </div>
          
          <div v-if="signalement.updated_at">
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              Dernière mise à jour
            </p>
            <p class="text-base md:text-lg text-gray-900">
              {{ formatDate(signalement.updated_at) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Description des faits -->
      <div v-if="signalement?.description_faits" class="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
        <h2 class="text-xl font-bold text-[#04406E] mb-4">Description des Faits</h2>
        <div class="space-y-4">
          <div v-if="signalement.description_faits.description">
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              Description
            </p>
            <p class="text-base md:text-lg text-gray-900 whitespace-pre-wrap">
              {{ signalement.description_faits.description }}
            </p>
          </div>
          <div v-if="signalement.description_faits.lieu || signalement.description_faits.date_faits" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-if="signalement.description_faits.lieu">
              <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
                Lieu
              </p>
              <p class="text-base md:text-lg text-gray-900">
                {{ signalement.description_faits.lieu }}
              </p>
            </div>
            <div v-if="signalement.description_faits.date_faits">
              <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
                Date des faits
              </p>
              <p class="text-base md:text-lg text-gray-900">
                {{ formatDate(signalement.description_faits.date_faits) }}
              </p>
            </div>
          </div>
          <div v-if="signalement.description_faits.temoins && signalement.description_faits.temoins.length > 0">
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              Témoins
            </p>
            <ul class="list-disc list-inside space-y-1">
              <li
                v-for="(temoin, index) in signalement.description_faits.temoins"
                :key="index"
                class="text-base text-gray-900"
              >
                {{ temoin }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Organisme mis en cause -->
      <div v-if="signalement?.organisme_mis_en_cause" class="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
        <h2 class="text-xl font-bold text-[#04406E] mb-4">Organisme Mis en Cause</h2>
        <div class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-if="signalement.organisme_mis_en_cause.nom">
              <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
                Nom
              </p>
              <p class="text-base md:text-lg text-gray-900 font-semibold">
                {{ signalement.organisme_mis_en_cause.nom }}
              </p>
            </div>
            <div v-if="signalement.organisme_mis_en_cause.type">
              <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
                Type
              </p>
              <p class="text-base md:text-lg text-gray-900">
                {{ signalement.organisme_mis_en_cause.type }}
              </p>
            </div>
          </div>
          <div v-if="signalement.organisme_mis_en_cause.adresse">
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
              Adresse
            </p>
            <p class="text-base md:text-lg text-gray-900">
              {{ signalement.organisme_mis_en_cause.adresse }}
            </p>
          </div>
          <div v-if="signalement.organisme_mis_en_cause.contact" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-if="signalement.organisme_mis_en_cause.contact.email">
              <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
                Email
              </p>
              <p class="text-base md:text-lg text-gray-900">
                {{ signalement.organisme_mis_en_cause.contact.email }}
              </p>
            </div>
            <div v-if="signalement.organisme_mis_en_cause.contact.telephone">
              <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
                Téléphone
              </p>
              <p class="text-base md:text-lg text-gray-900">
                {{ signalement.organisme_mis_en_cause.contact.telephone }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Informations de soumission -->
      <div v-if="signalement?.soumission" class="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
        <h2 class="text-xl font-bold text-[#04406E] mb-4">Informations de Soumission</h2>
        <div class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-if="signalement.soumission.date">
              <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
                Date de soumission
              </p>
              <p class="text-base md:text-lg text-gray-900">
                {{ formatDate(signalement.soumission.date) }}
              </p>
            </div>
            <div v-if="signalement.soumission.canal">
              <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">
                Canal
              </p>
              <p class="text-base md:text-lg text-gray-900">
                {{ signalement.soumission.canal }}
              </p>
            </div>
          </div>
          <div v-if="signalement.soumission.documents && signalement.soumission.documents.length > 0">
            <p class="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
              Documents joints
            </p>
            <DocumentViewer :documents="signalement.soumission.documents" />
          </div>
        </div>
      </div>

      <!-- Métadonnées -->
      <div v-if="signalement?.metadonnees?.tags && signalement.metadonnees.tags.length > 0" class="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
        <h2 class="text-xl font-bold text-[#04406E] mb-4">Métadonnées</h2>
        <div>
          <p class="text-sm text-gray-600 mb-2">Tags:</p>
          <div class="flex flex-wrap gap-2">
            <span 
              v-for="(tag, index) in signalement.metadonnees.tags" 
              :key="index"
              class="px-3 py-1 bg-[#04406E]/10 text-[#04406E] rounded-full text-sm font-medium"
            >
              {{ tag }}
            </span>
          </div>
        </div>
      </div>

      <!-- Timeline de l'historique des statuts -->
      <div v-if="signalement" class="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
        <h2 class="text-xl font-bold text-[#04406E] mb-6">Évolution du Signalement</h2>
        
        <!-- Timeline complète avec progression chronologique -->
        <div v-if="timelineItems.length > 0" class="relative">
          <!-- Ligne verticale de la timeline -->
          <div 
            class="absolute top-0 w-0.5 bg-gray-300" 
            :style="{ height: `calc(100% - 2rem)`, left: '15px' }"
          ></div>
          
          <!-- Items de la timeline (tous les statuts dans l'ordre chronologique) -->
          <div
            v-for="(item, index) in timelineItems"
            :key="`${item.statut}-${item.date}-${index}`"
            class="relative flex items-start"
            :class="{ 'mb-6': index < timelineItems.length - 1 }"
          >
            <div class="relative z-10">
              <StatusIcon 
                :statut="item.statut" 
                :is-current="index === timelineItems.length - 1"
              />
            </div>
            <div class="ml-6 flex-1 pb-6">
              <div class="flex items-center justify-between mb-1">
                <h3 class="text-base font-semibold" :class="getStatusTextClass(item.statut)">
                  {{ formatStatus(item.statut) }}
                  <span v-if="index === timelineItems.length - 1" class="ml-2 text-xs font-normal text-gray-500">
                    (Statut actuel)
                  </span>
                </h3>
                <span class="text-xs text-gray-500">
                  {{ formatDate(item.date) }}
                </span>
              </div>
              
              <!-- Flèche de transition si ce n'est pas le premier item -->
              <div v-if="index > 0" class="mb-2 flex items-center text-xs text-gray-400">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span class="italic">Transition depuis : {{ formatStatus(timelineItems[index - 1].statut) }}</span>
              </div>
              
              <!-- Description pour le statut NOUVEAU -->
              <p v-if="item.statut === 'NOUVEAU'" class="text-sm text-gray-600">
                Signalement créé et en attente de traitement
              </p>
              
              <!-- Commentaire de décision si disponible -->
              <div v-if="item.commentaire" class="mt-2">
                <p class="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border-l-4 border-[#04406E]">
                  <span class="font-semibold text-gray-900">Décision :</span> {{ item.commentaire }}
                </p>
              </div>
              
              <!-- ID du signalement pour le premier item -->
              <div v-if="index === 0 && signalement.id_signalement" class="mt-2">
                <span class="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                  {{ signalement.id_signalement }}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Message si aucun historique et statut NOUVEAU -->
        <div v-else-if="signalement.statut === 'NOUVEAU'" class="text-center py-8">
          <p class="text-gray-600">Le signalement vient d'être créé et est en attente de traitement.</p>
        </div>
      </div>

      <!-- Message si aucun signalement -->
      <div v-else-if="!signalement" class="bg-white rounded-2xl shadow-xl p-8 text-center">
        <p class="text-gray-600">Aucun signalement trouvé</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, defineComponent, h } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import type { Signalement } from '@/services/signalementService'
import DocumentViewer from '@/components/ui/DocumentViewer.vue'

const router = useRouter()
const route = useRoute()

const signalement = ref<Signalement | null>(null)

onMounted(() => {
  // Récupérer le signalement depuis sessionStorage
  const storedSignalement = sessionStorage.getItem('currentSignalement')
  
  if (storedSignalement) {
    try {
      signalement.value = JSON.parse(storedSignalement) as Signalement
      console.log('Signalement chargé:', signalement.value)
      console.log('Historique des statuts:', signalement.value?.analyse_referents?.historique_statuts)
      // Nettoyer le sessionStorage après récupération
      sessionStorage.removeItem('currentSignalement')
    } catch (error) {
      console.error('Erreur lors de la récupération du signalement:', error)
      router.push({ name: 'suivre-un-signalement' })
    }
  } else {
    // Si pas de signalement, rediriger vers la page de suivi
    router.push({ name: 'suivre-un-signalement' })
  }
})

// Trier l'historique par date (du plus ancien au plus récent pour la timeline)
const sortedHistorique = computed(() => {
  if (!signalement.value?.analyse_referents?.historique_statuts) {
    return []
  }
  
  const historique = [...signalement.value.analyse_referents.historique_statuts]
  
  // Trier par date (du plus ancien au plus récent)
  return historique.sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    return dateA - dateB
  })
})

// Construire la timeline complète avec tous les statuts dans l'ordre chronologique
const timelineItems = computed(() => {
  if (!signalement.value) return []
  
  const items: Array<{
    statut: string
    date: string
    commentaire?: string
    ancien_statut?: string
  }> = []
  
  // Toujours commencer par NOUVEAU avec la date de création
  const dateCreation = signalement.value.created_at || new Date().toISOString()
  items.push({
    statut: 'NOUVEAU',
    date: dateCreation
  })
  
  // Ajouter tous les changements de statut de l'historique dans l'ordre chronologique
  sortedHistorique.value.forEach(historique => {
    items.push({
      statut: historique.nouveau_statut,
      date: historique.date,
      commentaire: historique.commentaire,
      ancien_statut: historique.ancien_statut
    })
  })
  
  // Vérifier si le statut actuel est déjà le dernier dans la timeline
  const dernierItem = items[items.length - 1]
  const statutActuel = signalement.value.statut
  
  // Si le statut actuel n'est pas le dernier dans la timeline, l'ajouter
  if (statutActuel && dernierItem && statutActuel !== dernierItem.statut) {
    // Le statut actuel est différent du dernier historique, l'ajouter
    items.push({
      statut: statutActuel,
      date: signalement.value.updated_at || signalement.value.created_at || new Date().toISOString()
    })
  }
  
  // Trier tous les items par date pour s'assurer de l'ordre chronologique strict
  const sortedItems = items.sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    return dateA - dateB
  })
  
  // Supprimer les doublons de statut consécutifs (garder le plus récent avec commentaire)
  const uniqueItems: typeof items = []
  
  sortedItems.forEach((item) => {
    const existingIndex = uniqueItems.findIndex(existing => existing.statut === item.statut)
    
    if (existingIndex === -1) {
      // Premier occurrence de ce statut
      uniqueItems.push(item)
    } else {
      // Statut déjà vu, comparer les dates et garder le plus récent
      const existingItem = uniqueItems[existingIndex]
      const existingDate = new Date(existingItem.date).getTime()
      const newDate = new Date(item.date).getTime()
      
      if (newDate > existingDate) {
        // Remplacer par la version plus récente
        uniqueItems[existingIndex] = item
      } else if (newDate === existingDate && item.commentaire && !existingItem.commentaire) {
        // Même date mais cette version a un commentaire, la garder
        uniqueItems[existingIndex] = item
      }
    }
  })
  
  // Re-trier après suppression des doublons pour maintenir l'ordre chronologique
  return uniqueItems.sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    return dateA - dateB
  })
})

const formatStatus = (statut: string): string => {
  const statusMap: Record<string, string> = {
    'NOUVEAU': 'Nouveau',
    'EN_EXAMEN': 'En examen',
    'PRISE_EN_COMPTE': 'Prise en compte',
    'REJETE_AVANT_EXAMEN': 'Rejeté avant examen',
    'REJETE_APRES_EXAMEN': 'Rejeté après examen',
    'EN_DEMANDE_COMPLEMENT_PREUVE_APRES_EXAMEN': 'Demande de complément',
  }
  return statusMap[statut] || statut
}

// Détermine si un statut est négatif (rejet)
const isNegativeStatus = (statut: string): boolean => {
  return statut === 'REJETE_AVANT_EXAMEN' || statut === 'REJETE_APRES_EXAMEN'
}

// Détermine si un statut est une validation (pris en compte)
const isValidationStatus = (statut: string): boolean => {
  return statut === 'PRISE_EN_COMPTE'
}

// Détermine si un statut est en attente/en cours
const isPendingStatus = (statut: string): boolean => {
  return statut === 'NOUVEAU' || 
         statut === 'EN_EXAMEN' || 
         statut === 'EN_DEMANDE_COMPLEMENT_PREUVE_APRES_EXAMEN'
}

// Retourne la classe CSS pour le texte selon le statut
const getStatusTextClass = (statut: string): string => {
  if (isNegativeStatus(statut)) {
    return 'text-red-700'
  } else if (isValidationStatus(statut)) {
    return 'text-green-700'
  } else if (isPendingStatus(statut)) {
    return 'text-yellow-700'
  }
  return 'text-gray-900'
}

const getStatusClass = (statut: string): string => {
  const classes: Record<string, string> = {
    'NOUVEAU': 'bg-blue-100 text-blue-800',
    'EN_EXAMEN': 'bg-yellow-100 text-yellow-800',
    'PRISE_EN_COMPTE': 'bg-green-100 text-green-800',
    'REJETE_AVANT_EXAMEN': 'bg-red-100 text-red-800',
    'REJETE_APRES_EXAMEN': 'bg-red-100 text-red-800',
    'EN_DEMANDE_COMPLEMENT_PREUVE_APRES_EXAMEN': 'bg-orange-100 text-orange-800',
  }
  return classes[statut] || 'bg-gray-100 text-gray-800'
}

const formatDate = (dateString: string): string => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) {
      return dateString
    }
    
    // Vérifier si c'est une date avec heure (contient 'T' ou ':' suivi d'une heure)
    const hasTime = dateString.includes('T') || /\d{2}:\d{2}/.test(dateString)
    
    if (hasTime) {
      // Format complet avec date et heure
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    } else {
      // Format date seule
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date)
    }
  } catch {
    return dateString
  }
}

// Composant pour l'icône de statut dans la timeline
const StatusIcon = defineComponent({
  props: {
    statut: {
      type: String,
      required: true
    },
    isCurrent: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const isNegative = props.statut === 'REJETE_AVANT_EXAMEN' || props.statut === 'REJETE_APRES_EXAMEN'
    const isValidation = props.statut === 'PRISE_EN_COMPTE'
    const isPending = props.statut === 'NOUVEAU' || 
                      props.statut === 'EN_EXAMEN' || 
                      props.statut === 'EN_DEMANDE_COMPLEMENT_PREUVE_APRES_EXAMEN'

    return () => {
      const baseClasses = 'w-8 h-8 rounded-full flex items-center justify-center shadow-lg'
      const ringClasses = props.isCurrent ? 'ring-4 ring-offset-2' : ''
      
      if (isNegative) {
        // Croix rouge pour les rejets
        return h('div', {
          class: `${baseClasses} ${ringClasses} bg-red-500 ring-red-300`
        }, [
          h('svg', {
            class: 'w-5 h-5 text-white',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
          }, [
            h('path', {
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              'stroke-width': '2',
              d: 'M6 18L18 6M6 6l12 12'
            })
          ])
        ])
      } else if (isValidation) {
        // Coche verte pour la prise en compte
        return h('div', {
          class: `${baseClasses} ${ringClasses} bg-green-500 ring-green-300`
        }, [
          h('svg', {
            class: 'w-5 h-5 text-white',
            fill: 'none',
            stroke: 'currentColor',
            viewBox: '0 0 24 24'
          }, [
            h('path', {
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              'stroke-width': '2',
              d: 'M5 13l4 4L19 7'
            })
          ])
        ])
      } else if (isPending) {
        // Point jaune pour les statuts en attente/en cours
        return h('div', {
          class: `${baseClasses} ${ringClasses} bg-yellow-500 ring-yellow-300`
        }, [
          h('div', {
            class: 'w-3 h-3 rounded-full bg-white'
          })
        ])
      } else {
        // Point gris par défaut
        return h('div', {
          class: `${baseClasses} ${ringClasses} bg-gray-400 ring-gray-300`
        }, [
          h('div', {
            class: 'w-3 h-3 rounded-full bg-white'
          })
        ])
      }
    }
  }
})
</script>

